///
/// EarthBrowser
///
/// Created by Matt Giger on September 13, 2026
/// Copyright (c) 2026 Geodata Labs
///

/// Storage Manager tab for localStorage: view, edit, and delete keys.

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { logger } from '@/utils/LogDispatcher'
import { Edit, Save, Trash2, X } from 'lucide-react'
import { useState } from 'react'
import type { StorageTabProps } from '@/components/features/dev-tools/storage-manager/StorageManager'
import { formatBytes } from '@/components/features/dev-tools/storage-manager/storageScanner'

const PREVIEW_LENGTH = 200

function tryPrettifyJSON(value: string): { isJSON: boolean, formatted: string } {
	try {
		const parsed: unknown = JSON.parse(value)
		return { isJSON: true, formatted: JSON.stringify(parsed, null, 2) }
	} catch {
		return { isJSON: false, formatted: value }
	}
}

export function LocalStorageTab({ snapshot, busy, runAction }: StorageTabProps) {
	const [editingKey, setEditingKey] = useState<string | null>(null)
	const [editingValue, setEditingValue] = useState('')

	const totalSize = snapshot.localStorageItems.reduce((sum, item) => sum + item.size, 0)

	const onDelete = (key: string) => {
		if (!confirm(`Delete localStorage item "${key}"?\n\nThis action cannot be undone.`)) {
			return
		}
		void runAction(async () => {
			localStorage.removeItem(key)
			logger.info(`Deleted localStorage item: ${key}`)
		})
	}

	const onSave = () => {
		if (!editingKey) return
		const key = editingKey
		const value = editingValue
		setEditingKey(null)
		setEditingValue('')
		void runAction(async () => {
			localStorage.setItem(key, value)
			logger.info(`Updated localStorage item: ${key}`)
		})
	}

	return (
		<div className="space-y-3">
			<div className="text-xs text-muted-foreground">
				{snapshot.localStorageItems.length} keys • {formatBytes(totalSize)} total (sorted largest first)
			</div>

			{snapshot.localStorageItems.length === 0 && (
				<Card>
					<CardContent className="py-6 text-center text-sm text-muted-foreground">
						No localStorage items found
					</CardContent>
				</Card>
			)}

			{snapshot.localStorageItems.map(item => {
				const isEditing = editingKey === item.key
				const { isJSON, formatted } = tryPrettifyJSON(item.value)
				const isTruncated = formatted.length > PREVIEW_LENGTH
				const preview = isTruncated ? formatted.substring(0, PREVIEW_LENGTH) + '…' : formatted

				if (isEditing) {
					return (
						<div key={item.key} className="p-3 border border-slate-700 rounded space-y-2">
							<div className="flex items-center justify-between">
								<div className="text-sm font-medium font-mono break-all">{item.key}</div>
								<div className="flex items-center gap-2">
									<Button variant="ghost" size="sm" onClick={onSave} title="Save changes">
										<Save className="h-3 w-3 text-green-500" />
									</Button>
									<Button
										variant="ghost"
										size="sm"
										onClick={() => {
											setEditingKey(null)
											setEditingValue('')
										}}
										title="Cancel editing"
									>
										<X className="h-3 w-3" />
									</Button>
								</div>
							</div>
							<textarea
								value={editingValue}
								onChange={(e) => setEditingValue(e.target.value)}
								className="w-full min-h-[200px] p-2 text-xs font-mono bg-secondary/50 rounded border border-slate-700 resize-y"
								autoFocus
							/>
							<div className="text-xs text-muted-foreground">
								{editingValue.length.toLocaleString()} characters
							</div>
						</div>
					)
				}

				return (
					<div key={item.key} className="flex items-start justify-between p-3 border border-slate-700 rounded gap-3">
						<div className="space-y-1 flex-1 min-w-0">
							<div className="flex items-center gap-2">
								<div className="text-sm font-medium font-mono break-all">{item.key}</div>
								{isJSON && <Badge variant="outline" className="text-xs">JSON</Badge>}
							</div>
							<div className="text-xs text-muted-foreground">
								{formatBytes(item.size)} • {item.value.length.toLocaleString()} characters
							</div>
							<div className="text-xs font-mono text-muted-foreground bg-secondary/50 p-2 rounded break-all whitespace-pre-wrap">
								{preview}
								{isTruncated && <span className="text-blue-400 ml-1">[truncated]</span>}
							</div>
						</div>
						<div className="flex flex-col gap-2 shrink-0">
							<Button
								variant="ghost"
								size="sm"
								onClick={() => {
									setEditingKey(item.key)
									setEditingValue(formatted)
								}}
								disabled={busy}
								title={`Edit "${item.key}"`}
							>
								<Edit className="h-3 w-3" />
							</Button>
							<Button
								variant="ghost"
								size="sm"
								onClick={() => onDelete(item.key)}
								disabled={busy}
								title={`Delete "${item.key}"`}
							>
								<Trash2 className="h-3 w-3" />
							</Button>
						</div>
					</div>
				)
			})}
		</div>
	)
}
